import React, { useEffect, useRef, useState } from 'react';
import './LeafletMap.css';
import { Link } from 'react-router-dom';
import ThemeTypoModal from '../components/Admin/Dashboard/Project/ThemetypoModal';

const fetchWithCache = (() => {
  const cache = new Map();
  return async (url, ttl = 1000 * 60 * 20) => {
    const now = Date.now();
    const cached = cache.get(url);
    if (cached && now - cached.timestamp < ttl) return cached.data;
    const res = await fetch(url);
    const data = await res.json();
    cache.set(url, { data, timestamp: now });
    return data;
  };
})();


const LeafletMap = ({ masterData, onFeatureClick }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const projectLayerRef = useRef(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const projectsRef = useRef([]);
  const typologyRef = useRef(null);
  const districtLayersRef = useRef([]);
  const projectMarkersRef = useRef({});
  const [showStateLayer, setShowStateLayer] = useState(true);
  const [showTypologyLayer, setShowTypologyLayer] = useState(true);
  const [showDistrictLayer, setShowDistrictLayer] = useState(false);
  const [showLayerControls, setShowLayerControls] = useState(false);
  const stateRef = useRef(null);
  const districtRef = useRef(null);
  const [filteredTheme, setFilteredTheme] = useState([]);
  const [filteredTypology, setFilteredTypology] = useState([]);
  const [typologiesData, setTypologiesData] = useState([]);
  const [themesData, setThemesData] = useState([]);
  const typologyPolygonsRef = useRef({});
  const [filters, setFilters] = useState({
    project: '',
    theme: '',
    typology: '',
    district: '',
    state: ''
  });

  const defaultCenter = [22.9, 82.85];
  const defaultZoom = 5;
  const WORKSPACE = 'Acwa';
  const WMS_URL = `https://acwadam.org/geoserver/${WORKSPACE}/wms`;
  const WFS_URL = `https://acwadam.org/geoserver/${WORKSPACE}/ows?service=WFS&version=1.0.0&request=GetFeature&outputFormat=application/json&`;

  useEffect(() => {
    const typologyData = JSON.parse(localStorage.getItem('typologyData'));
    const themeData = JSON.parse(localStorage.getItem('themeData'));
    setTypologiesData(typologyData);
    setThemesData(themeData);
    const loadLeaflet = async () => {
      if (window.L) return;
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css';
      document.head.appendChild(css);
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
      return new Promise((resolve) => {
        script.onload = resolve;
        document.head.appendChild(script);
      });
    };
    let isMounted = true;
    const initializeMap = async () => {
      await loadLeaflet();
      const L = window.L;
      if (mapInstanceRef.current) return;
      const map = L.map(mapRef.current, {
        center: defaultCenter,
        zoom: defaultZoom
      });
      map.whenReady(() => {
        if (!isMounted) return;
        L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=pk.eyJ1Ijoia3dyaXNhY2l3cm0iLCJhIjoiY2xma3p3NmpoMDBhaTNwbnV1NnVkMGp2ZCJ9.1ASKnwxbjSZxZGiXn0xl4Q', {
          maxZoom: 14,
          id: 'mapbox/outdoors-v11',
          tileSize: 512,
          zoomOffset: -1
        }).addTo(map);
      })
      const loadProjects = fetchWithCache(`${WFS_URL}typeName=${WORKSPACE}:project_details`)//.then(res => res.json());
      if (!isMounted) return;
      loadProjects.then(projectData => {
        const enhancedProjects = projectData.features.map(p => {
          // Parse comma-separated state and district IDs
          const stateIds = p.properties.state?.split(',') || [];
          const districtIds = p.properties.district?.split(',') || [];
          const typologyIds = p.properties.typology?.split(',') || [];
          const themeIds = p.properties.theme?.split(',') || [];

          // Map state IDs to names
          const stateNames = stateIds.map(sid => {
            const matchedState = masterData?.state?.find(st => st.id_mststate?.toString() === sid.trim());
            return matchedState?.statename;
          }).filter(Boolean); // Remove nulls

          // Map district IDs to names (optional)
          const districtNames = districtIds.map(did => {
            const matchedDistrict = masterData?.district?.find(dt => dt.id_mstdistrict?.toString() === did.trim());
            return matchedDistrict?.districtname;
          }).filter(Boolean);

          // Map typology IDs to names (optional)
          const typologyNames = typologyIds.map(did => {
            return typologyData?.find(dt => dt.id?.toString() === did.trim()).typology;
          }).filter(Boolean);

          const typo = typologyIds.map(did => {
            return typologyData?.find(dt => dt.id?.toString() === did.trim());
          }).filter(Boolean);

          const typoId = typologyIds.map(did => {
            return typologyData?.find(dt => dt.id?.toString() === did.trim())?.geoId;
          }).filter(Boolean);

          // Map typology IDs to names (optional)
          const themeNames = themeIds.map(did => {
            return themeData?.find(dt => dt.id?.toString() === did.trim()).theme;
          }).filter(Boolean);

          const thme = themeIds.map(did => {
            return themeData?.find(dt => dt.id?.toString() === did.trim());
          }).filter(Boolean);

          setFilteredTypology(pre => [...new Set([...pre, ...typo])]);
          setFilteredTheme(pre => [...new Set([...pre, ...thme])]);

          return {
            ...p,
            properties: {
              ...p.properties,
              statenames: stateNames, // array
              districtnames: districtNames, // optional
              typologyNames,
              themeNames,
              typoId
            }
          };
        });
        setProjects(enhancedProjects);
        projectsRef.current = enhancedProjects;
        setFilteredProjects(enhancedProjects);
        const projectLayer = L.geoJSON(projectData, {
          pointToLayer: (feature, latlng) => {
            const marker = L.circleMarker(latlng, {
              radius: 6,
              fillColor: '#007bff',
              color: '#fff',
              weight: 1,
              opacity: 1,
              fillOpacity: 0.8
            });
            projectMarkersRef.current[
              `${feature.properties.id}_${feature.properties.lat}_${feature.properties.longitude}`
            ] = marker;
            return marker
          },
          onEachFeature: (feature, layer) => {
            const props = feature.properties;
            const p = projectsRef.current?.find(m => m?.id == feature?.id)?.properties;
            layer.bindTooltip(`<strong>Title:</strong> ${props.title}<br/><strong>Location:</strong> ${Array.isArray(p?.statenames) ? p?.statenames.join(', ') : p?.statenames}
            <br/> <strong>Theme:</strong> ${Array.isArray(p?.themeNames) ? p?.themeNames.join(', ') : p?.themeNames} <br/>
            <strong>Typology:</strong> ${Array.isArray(p?.typologyNames) ? p?.typologyNames.join(', ') : p?.typologyNames}`);
            layer.on('click', () => {
              window.location.href = `/project-details/${props.id}`;
            });
          }
        });
        projectLayerRef.current = projectLayer;
        if (isMounted) projectLayer.addTo(map);
      });

      try {
        const loadState = fetchWithCache(`${WFS_URL}typeName=${WORKSPACE}:State_Boundary`)//.then(res => res.json());
        const loadTypology = fetchWithCache(`${WFS_URL}typeName=${WORKSPACE}:Typology-layer&srsName=EPSG:4326`)//.then(res => res.json());
        if (!isMounted) return;
        loadState.then(stateData => {
          const stateLayer = L.geoJSON(stateData, {
            style: (feature) => {
              const stateName = feature.properties.STATE || '';
              const hasProjects = projectsRef.current.some(p =>
                p.properties.statenames?.some(
                  s => s.toLowerCase() === stateName.toLowerCase()
                )
              );
              return {
                color: hasProjects ? 'orange' : '#3d3e42ff',
                weight: 2,
                fillOpacity: hasProjects ? 0.3 : 0.1
              };
            },
            onEachFeature: (feature, layer) => {
              const stateName = feature.properties.STATE || 'Unknown';
              layer.bindTooltip(`State: ${stateName}`);
              layer.on('click', () => {
                const clickedStateName = feature.properties.STATE || '';
                const filteredProjects = projectsRef.current.filter(p =>
                  p.properties.statenames?.some(
                    s => s.toLowerCase() === clickedStateName.toLowerCase()
                  )
                );
                setFilteredProjects(filteredProjects);
                setFilters(prev => ({ ...prev, state: stateName }));
                highlightDistrictsForState(stateName);
              });
            }
          });
          stateRef.current = stateLayer;
          if (showStateLayer && isMounted) {
            map.whenReady(() => {
              stateLayer.addTo(map);
            });
          }
        });
        const legendControl = L.control({ position: 'bottomright' });
        legendControl.onAdd = function () {
          const div = L.DomUtil.create('div', 'typo-info legend');
          div.innerHTML += `<h4>Typology</h4>`;
          const colorMap = {
            '2': { name: 'Alluvial', color: '#fcfebe' },
            '3': { name: 'Crystalline', color: '#f9c0be' },
            '5': { name: 'Sedimentary (Hard)', color: '#326ff1' },
            '6': { name: 'Sedimentary (Soft)', color: '#94dcf6' },
            '8': { name: 'Volcanic', color: '#74b776' }
          };
          Object.entries(colorMap).forEach(([id, { name, color }]) => {
            div.innerHTML += `
      <i style="background:${color}; width: 12px; height: 12px; display:inline-block; margin-right:5px;"></i>
      <span>${name}</span><br/>
    `;
          });
          return div;
        };
        loadTypology.then(typo => {
          const colorMap = {
            '2': '#fcfebe',
            '3': '#f9c0be',
            '5': '#326ff1',
            '6': '#94dcf6',
            '8': '#74b776'
          };
          const typologyGroups = {};
          const typologyLayer = L.geoJSON(typo, {
            style: feature => {
              const id = feature.properties?.RuleID?.toString();
              const color = colorMap[id] || 'gray';
              return {
                color: color,
                weight: 1,
                fillOpacity: 0.9
              };
            },
            onEachFeature: (feature, layer) => {
              const typologyName = feature.properties.Formations || 'Unknown';
              const type = feature.properties.RuleID?.toString();

              if (!typologyGroups[type]) {
                typologyGroups[type] = L.layerGroup();
              }
              typologyGroups[type].addLayer(layer);

              layer.bindTooltip(`Typology: ${typologyName}`);
              layer.on('click', () => {
                setFilteredProjects(
                  projectsRef.current.filter(p => p.properties?.typoId?.some(m => (m == type)))
                );
              });
            }
          });
          typologyRef.current = typologyLayer;
          typologyPolygonsRef.current = typologyGroups;
          if (typologyLayer && isMounted) {
            map.whenReady(() => {
              typologyLayer.addTo(map);
              legendControl.addTo(map);
            });
          }
        });
      } catch (err) {
        console.error('Failed to fetch:', err);
      }

      mapInstanceRef.current = map;

      if (!isMounted) return;
      map.whenReady(() => {
        const homeControl = L.Control.extend({
          options: {
            position: 'topleft'
          },
          onAdd: function () {
            const container = L.DomUtil.create('div', 'leaflet-control-home leaflet-bar');
            container.innerHTML = `<button 
              id="home-button" 
              class="leaflet-bar-part leaflet-bar-part-single" 
              title="Home" 
              style="
                width: 30px;
                height: 30px;
                line-height: 30px;
                text-align: center;
                cursor: pointer;
                background: white;
                border: none;
                font-size: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
              "
            >
              🏠
            </button>`;
            container.title = 'Reset View';
            container.onclick = function () {
              resetToDefault();
            };
            return container;
          }
        });
        map.addControl(new homeControl());
      })
    };

    initializeMap();

    return () => {
      isMounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const highlightDistrictsForState = (clickedStateName) => {
    districtLayersRef.current.forEach(({ layer }) => {
      layer.setStyle({
        color: '#3366ff',
        weight: 2,
        fillOpacity: 0.1
      });
    });

    // Get districts with at least one project in the clicked state
    const districtsWithProjects = new Set(
      projectsRef.current
        .filter(p =>
          Array.isArray(p.properties.statenames) &&
          p.properties.statenames.some(
            name => name.toLowerCase() === clickedStateName.toLowerCase()
          )
        ).flatMap(p =>
          (p.properties.district || "")
            .split(",")
            .map(did => did.trim())
            .filter(Boolean)
        )
    );

    // Highlight those districts
    districtLayersRef.current.forEach(({ id, layer, stateId }) => {
      if (districtsWithProjects.has(id?.toString())) {
        layer.setStyle({
          color: 'orange',
          weight: 4,
          fillOpacity: 0.4
        });
      }
    });
  };

  const toggleDistrictLayer = async () => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (showDistrictLayer) {
      if (districtRef.current) {
        map.removeLayer(districtRef.current);
      }
      setShowDistrictLayer(false);
    } else {
      if (!districtRef.current) {
        const districtData = await fetchWithCache(`${WFS_URL}typeName=${WORKSPACE}:District_Boundary`);
        const districtLayer = window.L.geoJSON(districtData, {
          style: (feature) => {
            const districtId = feature.properties.OBJECTID?.toString();
            const hasProjects = projectsRef.current.some(p =>
              (p.properties.district || '').split(',').map(id => id.trim()).includes(districtId)
            );
            return {
              color: hasProjects ? 'red' : '#00cc66',
              weight: 3,
              fillOpacity: hasProjects ? 0.3 : 0.1
            };
          },
          onEachFeature: (feature, layer) => {
            const districtName = feature.properties.DISTRICT || 'Unknown';
            const districtId = feature.properties.OBJECTID;
            const parentState = masterData?.state?.find(m => m?.id_mststate == feature.properties.STATE_LGD)?.statename;
            districtLayersRef.current.push({
              id: districtId,
              stateId: feature.properties.STATE_LGD,
              layer
            });
            layer.bindTooltip(`District: ${districtName}<br/>State: ${parentState}`);
            layer.on('click', () => {
              setFilteredProjects(
                projectsRef.current.filter(p => p.properties.district?.split(',')?.some(m => (m == districtId)))
              );
              setFilters(prev => ({
                ...prev,
                district: districtName,
                state: parentState
              }));
            });
          }
        });
        districtRef.current = districtLayer;
      }
      districtRef.current.addTo(map);
      setShowDistrictLayer(true);
    }
  };

  const filteredData = (selectedId) => {
    Object.entries(projectMarkersRef.current).forEach(([markerId, marker]) => {
      const isMatch = markerId.toString() === selectedId.toString();

      marker.setStyle({
        fillColor: isMatch ? 'red' : '#007bff',
        color: '#fff',
        fillOpacity: isMatch ? 1 : 0.5,
        radius: isMatch ? 8 : 6,
      });
    });
  };

  const resetToDefault = () => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Reset map view to default center and zoom
    map.setView(defaultCenter, defaultZoom);

    // Reset all filters
    setFilters({
      project: '',
      theme: '',
      typology: '',
      district: '',
      state: ''
    });

    // Reset filtered projects to show all projects
    setFilteredProjects(projectsRef.current);

    // Reset project markers to default style
    Object.entries(projectMarkersRef.current).forEach(([markerId, marker]) => {
      marker.setStyle({
        fillColor: '#007bff',
        color: '#fff',
        fillOpacity: 0.8,
        radius: 6,
      });
    });

    // Remove all individual typology groups
    Object.values(typologyPolygonsRef.current || {}).forEach(layerGroup => {
      if (map.hasLayer(layerGroup)) {
        map.removeLayer(layerGroup);
      }
    });

    // Reset typology layer to default (show all if enabled)
    if (typologyRef.current) {
      if (map.hasLayer(typologyRef.current)) {
        map.removeLayer(typologyRef.current);
      }
      if (showTypologyLayer) {
        typologyRef.current.addTo(map);
      }
    }

    // Reset state layer
    if (stateRef.current) {
      if (map.hasLayer(stateRef.current)) {
        map.removeLayer(stateRef.current);
      }
      if (showStateLayer) {
        stateRef.current.addTo(map);
      }
    }

    // Reset district layer
    if (districtRef.current) {
      if (map.hasLayer(districtRef.current)) {
        map.removeLayer(districtRef.current);
      }
    }

    //  Reset district layer styles if they exist
    districtLayersRef.current.forEach(({ layer }) => {
      layer.setStyle({
        color: '#3366ff',
        weight: 2,
        fillOpacity: 0.1
      });
    });

    // Reset layer control states to default
    setShowStateLayer(true);
    setShowTypologyLayer(true);
    setShowDistrictLayer(false);
    setShowLayerControls(false);
  };

  return (
    <div className="container">
      <div className="row my-3">
        <div className='col-md-3'>
          <span><b>Project</b></span>
          <select className='form-select w-full whitespace-normal break-words pe-5' value={filters.project} onChange={(e) => {
            const title = e.target.value;
            setFilters(f => ({ ...f, project: title }));
            const selected = projects.find(p => p.properties.title === title);
            if (selected) {
              const { lat, longitude } = selected.properties;
              if (lat && longitude && mapInstanceRef.current) {
                mapInstanceRef.current.setView([lat, longitude], 10);
                filteredData(selected.properties.id);
                setFilteredProjects(projectsRef.current.filter(p => p.properties?.id === selected.properties.id))
              }
            }
          }}>
            <option value="">Select Project</option>
            {[...new Set(projects.map(p => p.properties.title))]?.sort((a, b) => a?.localeCompare(b))?.map((title, i) => (
              <option key={i} value={title}>{title}</option>
            ))}
          </select>
        </div>
        <div className='col-md-3'>
          <span><b>District</b></span>
          <select className='form-select w-full whitespace-normal break-words pe-5' value={filters.district} onChange={(e) => {
            const selectedId = e.target.value;
            const districtMatch = masterData?.district?.find(d => d.id_mstdistrict?.toString() === selectedId);
            const stateName = districtMatch?.state_name || '';
            setFilters(f => ({ ...f, district: selectedId, state: stateName }));
            const selectedProjects = projectsRef.current.filter(p =>
              p.properties.district.split(',').some(m => m === selectedId)
            );
            setFilteredProjects(selectedProjects);
          }}>
            <option value="">Select District</option>
            {masterData?.district?.filter(p => [...new Set(projects.flatMap(p => p.properties.district?.split(',') || []))].includes(p?.id_mstdistrict?.toString()))
              ?.sort((a, b) => a?.districtname?.localeCompare(b?.districtname))?.map((dist, i) => (
                <option key={dist?.id_mstdistrict} value={dist?.id_mstdistrict}>{dist.districtname}</option>
              ))}
          </select>
        </div>
        <div className='col-md-3'>
          <span><b>Theme</b></span><i className="bi bi-info-circle text-primary ms-2" onClick={() => {
            onFeatureClick(themesData, 'Theme')
          }
          } style={{ cursor: 'pointer' }}></i>
          <select className='form-select w-full whitespace-normal break-words pe-5' value={filters.theme} onChange={(e) => {
            setFilteredProjects(projectsRef.current.filter(p => p.properties?.theme?.split(',')?.some(m => (m == e.target.value))))
          }}>
            <option value="">Select Theme</option>
            {filteredTheme?.sort((a, b) => a?.theme?.localeCompare(b?.theme))?.map((theme, i) => (
              <option key={i} value={theme.id}>{theme.theme}</option>
            ))}
          </select>
        </div>
        <div className='col-md-3'>
          <span><b>Typology</b></span><i className="bi bi-info-circle text-primary ms-2" onClick={() => {
            onFeatureClick(typologiesData, 'Typology')
          }
          } style={{ cursor: 'pointer' }}></i>
          <select className='form-select w-full whitespace-normal break-words pe-5' value={filters.typology} onChange={(e) => {
            const selectedId = e.target.value;
            setFilters(prev => ({ ...prev, typology: selectedId }));
            setFilteredProjects(
              projectsRef.current.filter(p =>
                p.properties?.typoId?.some(m => m == selectedId)
              )
            );
            const map = mapInstanceRef.current;
            if (!map) return;
            if (typologyRef.current && map.hasLayer(typologyRef.current)) {
              map.removeLayer(typologyRef.current);
            }

            Object.values(typologyPolygonsRef.current || {}).forEach(layerGroup => {
              if (map.hasLayer(layerGroup)) {
                map.removeLayer(layerGroup);
              }
            });

            if (selectedId && typologyPolygonsRef.current?.[selectedId]) {
              typologyPolygonsRef.current[selectedId].addTo(map);
            } else if (!selectedId) {
              if (typologyRef.current && showTypologyLayer) {
                typologyRef.current.addTo(map);
              }
            }
          }}>
            <option value="">Select Typology</option>
            {filteredTypology?.sort((a, b) => a?.typology?.localeCompare(b?.typology))?.map((typ, i) => (
              <option key={i} value={typ?.geoId}>{typ?.typology}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="row">
        <div className="col-md-7" style={{ height: '750px' }} ref={mapRef}></div>
        <div style={{ position: 'absolute' }}>
          <div style={{ position: 'absolute', top: '8rem', left: '14px', zIndex: 1000 }}>
            <button
              onClick={() => setShowLayerControls(!showLayerControls)}
              className="custom-layer-toggle-btn"
              aria-label="Toggle layers"
            >
              <i className="fa-solid fa-sliders"></i>
            </button>
          </div>
          {showLayerControls && (
            <div
              className="custom-layer-controls"
              style={{ position: 'absolute', top: '9.7rem', left: '28px', zIndex: 1000 }}
            >
              <label>
                <input
                  type="checkbox"
                  checked={showStateLayer}
                  onChange={() => {
                    const map = mapInstanceRef.current;
                    if (!map || !stateRef.current) return;
                    if (showStateLayer) {
                      map.removeLayer(stateRef.current);
                    } else {
                      stateRef.current.addTo(map);
                    }
                    setShowStateLayer(prev => !prev);
                  }}
                />
                {' '}
                State
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showDistrictLayer}
                  onChange={toggleDistrictLayer}
                />
                {' '}
                District
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showTypologyLayer}
                  onChange={() => {
                    const map = mapInstanceRef.current;
                    if (!map || !typologyRef.current) return;
                    if (showTypologyLayer) {
                      map.removeLayer(typologyRef.current);
                    } else {
                      typologyRef.current.addTo(map);
                    }
                    setShowTypologyLayer(prev => !prev);
                  }}
                />
                {' '}
                Typology
              </label>
            </div>
          )}
        </div>
        <div className="border col-md-5">
          <div>
            <h6 className="text-lg font-semibold my-2"><b>Filtered Projects</b>{filters.typology && `(${filteredTypology?.find(m => m.geoId == filters?.typology)?.typology})`}</h6>
            <table className='table table-bordered' style={{ maxHeight: '650px', overflow: 'none' }}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Locations</th>
                  <th className='text-center'>Action</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(
                  new Map(
                    filteredProjects.map(p => [p.properties.id, p])
                  ).values()
                ).map((proj, i) => (
                  <tr key={i}>
                    <td>{proj.properties.title}</td>
                    <td>{Array.isArray(proj.properties.statenames) ? proj.properties.statenames.join(', ') : proj.properties.statenames}</td>
                    <td className='text-center'>
                      <Link to={`/project-details/${proj.properties.id}`} key={i}>
                        <i className='bi bi-eye text-primary'
                          style={{ cursor: 'pointer' }}></i>
                      </Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className='text-end'>
              <Link to="/projects">View all</Link>
            </div>
          </div>
        </div>
        {/* {showViewModal && (
        <ThemeTypoModal selectionType={selectionType} selectedData={selectedData} setShowViewModal={setShowViewModal} />
      )} */}
      </div>
    </div>
  );
};

export default LeafletMap;
